import React, { forwardRef } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import tw, { css } from 'twin.macro';
import { ChevronRightIcon as ChevronRight } from '@heroicons/react/20/solid';
import NodeIcon from 'components/Tree2/NodeIcon';
import { ITreeNode, TreeEventHandler } from './types';
import { Tree2 } from 'components/Tree2';

interface Props<T> {
  wrapperRef: any;
  handleProps: any;
  isOver: boolean;

  onNodeCollapse?: TreeEventHandler<T>;
  onNodeExpand?: TreeEventHandler<T>;
  node?: ITreeNode<T>;
  onNodeClick: TreeEventHandler<T>;
  path: number[];
  index: number;
  isDragging?: boolean;
  isDraggingItem?: boolean;
  isPlaceholder?: boolean;
  disableOver?: boolean;
  style?: React.CSSProperties;
  RenderLabelComponent?: React.ComponentType<{
    className?: string;
    node: ITreeNode<T>;
  }>;
}

export const TreeItem = forwardRef<HTMLDivElement, Props<any>>(
  (props: Props<any>, ref) => {
    const {
      node,
      onNodeCollapse,
      onNodeExpand,
      index,
      onNodeClick,
      path,
      handleProps,
      wrapperRef,
      isDraggingItem,
      isOver,
      isPlaceholder,
      style = {},
      RenderLabelComponent,
    } = props;
    if (isPlaceholder) {
      return (
        <div ref={wrapperRef} css={[tw`relative pl-8 w-full`]}>
          <div
            css={[tw`relative`, isOver && tw`bg-brand-intense! h-0.5 `]}
          ></div>
        </div>
      );
    }
    if (!node) {
      return null;
    }
    const defaultItemStyle = [
      tw`truncate flex-grow select-none`,
      typeof node.label === 'string' &&
        (node.label || '').startsWith('--- ') &&
        tw`text-neutral-intense`,
    ];

    const LabelComponent = RenderLabelComponent ? (
      <RenderLabelComponent css={defaultItemStyle} node={node} />
    ) : (
      <span css={defaultItemStyle}>{node.label}</span>
    );

    return (
      <div tw="relative">
        <div css={[tw`flex items-center`, props.isDragging && tw`opacity-25`]}>
          <div tw="w-full">
            <Disclosure>
              <Disclosure.Button
                ref={wrapperRef}
                style={{ ...style, ...(node?.style || {}) }}
                css={[
                  isOver && tw`ring-2 rounded-md ring-brand-intense`,
                  isOver && isPlaceholder && tw`bg-brand-intense!`,
                  tw`appearance-none w-full`,
                  isDraggingItem && tw`cursor-grabbing`,
                ]}
                {...handleProps}
                as="div"
              >
                <div
                  role="presentation"
                  ref={ref}
                  key={node.id}
                  className="group"
                  onClick={e => onNodeClick(node, [...path, index], e)}
                  css={[
                    tw`mb-1 flex items-center pl-1 text-sm font-medium rounded-md h-8 relative`,
                    !isDraggingItem &&
                      !node.isSelected &&
                      tw`hover:bg-neutral-supershy`,
                    node.isSelected && tw`bg-neutral-quiet`,
                    !node.hasCaret && tw`[@media(hover:none)]:ml-7`,
                  ]}
                >
                  {node.hasCaret && (
                    <div
                      css={[
                        tw`size-6 rounded-md`,
                        css`
                          @media (hover: none) {
                            ${tw`mr-1`};
                          }
                          @media (hover: hover) {
                            ${tw`absolute invisible group-hover:visible bg-neutral-quiet hover:bg-neutral-moderate`};
                          }
                        `,
                      ]}
                    >
                      <ChevronRight
                        onClick={e => {
                          if (node.isExpanded && onNodeCollapse) {
                            onNodeCollapse(node, [...path, index], e as any);
                          } else if (onNodeExpand) {
                            onNodeExpand(node, [...path, index], e as any);
                          }
                          e.stopPropagation();
                        }}
                        css={[
                          !node.hasCaret && tw`invisible`,
                          tw`shrink-0 size-6`,
                          !node.isExpanded &&
                            tw`transform rotate-0 transition-transform duration-200`,
                          node.isExpanded &&
                            tw`transform rotate-90 transition-transform duration-200`,
                          (node?.childNodes || []).length === 0 &&
                            tw`invisible`,
                          css`
                            transition-timing-function: cubic-bezier(
                              0.4,
                              1,
                              0.75,
                              0.9
                            );
                          `,
                        ]}
                      />
                    </div>
                  )}
                  <NodeIcon icon={node.icon} />
                  {LabelComponent}
                  {node.secondaryLabel && !isDraggingItem ? (
                    <span
                      css={[
                        tw`text-xs rounded-lg scale-75`,
                        css`
                          @media (hover: hover) {
                            ${tw`invisible group-hover:visible hover:bg-neutral-quiet`};
                            button {
                              ${tw`hover:bg-neutral-moderate`};
                            }
                          }
                        `,
                      ]}
                    >
                      {node.secondaryLabel}
                    </span>
                  ) : null}
                </div>
              </Disclosure.Button>

              <div>
                <Transition
                  show={!!node.isExpanded}
                  enter="enter"
                  enterFrom="enterFrom"
                  enterTo="enterTo"
                  leave="leave"
                  leaveFrom="leaveFrom"
                  leaveTo="leaveTo"
                >
                  <Disclosure.Panel static>
                    <div css={[tw`pl-4 [@media(hover:hover)]:pl-2.5`]}>
                      <Tree2
                        {...props}
                        nodes={node.childNodes}
                        path={[...path, index]}
                      />
                    </div>
                  </Disclosure.Panel>
                </Transition>
              </div>
            </Disclosure>
          </div>
        </div>
      </div>
    );
  },
);
